// Platform catalog editing (PUT /platforms/{id} on the auth API). The PUT is a full replace, so the
// payload always carries every field.

import { isWebUrl } from '@/util/url-util';

// Existing sub-platform ids are lowercase slugs (bonfire's `admtl`, `bcit`, ...).
const SUB_PLATFORM_ID_PATTERN = /^[a-z0-9][a-z0-9_-]*$/;

export const PLATFORM_ERROR = Object.freeze({
  REQUIRED: 'required',
  URL: 'url',
  ID_FORMAT: 'id-format',
  ID_DUPLICATE: 'id-duplicate',
});

const trim = (value) => (typeof value === 'string' ? value.trim() : '');
const trimOrNull = (value) => trim(value) || null;

const toRegionPayload = (region) => ({ code: region.code, nameFr: region.nameFr, nameEn: region.nameEn });

export function toSubPlatformPayload(subPlatform) {
  return {
    id: trim(subPlatform.id),
    nameFr: trim(subPlatform.nameFr),
    nameEn: trim(subPlatform.nameEn),
    descriptionFr: trimOrNull(subPlatform.descriptionFr),
    descriptionEn: trimOrNull(subPlatform.descriptionEn),
    url: trimOrNull(subPlatform.url),
    regions: (subPlatform.regions || []).map(toRegionPayload),
  };
}

/**
 * The body PUT /platforms/{id} expects. Also the canonical form two versions of a platform are
 * compared in, so blank vs null descriptions or extra keys (tenant access merged in by
 * `tenantPlatforms`) never count as a change.
 */
export function toPlatformPayload(platform) {
  return {
    id: platform.id,
    nameFr: trim(platform.nameFr),
    nameEn: trim(platform.nameEn),
    tags: [...(platform.tags || [])],
    descriptionFr: trimOrNull(platform.descriptionFr),
    descriptionEn: trimOrNull(platform.descriptionEn),
    url: trimOrNull(platform.url),
    regions: (platform.regions || []).map(toRegionPayload),
    subPlatforms: (platform.subPlatforms || []).map(toSubPlatformPayload),
  };
}

export function isSamePlatform(a, b) {
  return JSON.stringify(toPlatformPayload(a)) === JSON.stringify(toPlatformPayload(b));
}

/**
 * Errors keyed by field path (`nameFr`, `subPlatforms.3.id`), valued with a PLATFORM_ERROR. Empty
 * when the platform can be saved.
 */
export function validatePlatform(platform) {
  const errors = {};
  const payload = toPlatformPayload(platform);

  if (!payload.nameFr) errors.nameFr = PLATFORM_ERROR.REQUIRED;
  if (!payload.nameEn) errors.nameEn = PLATFORM_ERROR.REQUIRED;
  if (payload.url && !isWebUrl(payload.url)) errors.url = PLATFORM_ERROR.URL;

  // A Map, not an object: an id like `constructor` would hit an inherited key and never count as 2.
  const idCounts = payload.subPlatforms.reduce(
    (counts, { id }) => counts.set(id, (counts.get(id) || 0) + 1),
    new Map(),
  );

  payload.subPlatforms.forEach((subPlatform, index) => {
    const path = `subPlatforms.${index}`;
    if (!subPlatform.id) errors[`${path}.id`] = PLATFORM_ERROR.REQUIRED;
    else if (!SUB_PLATFORM_ID_PATTERN.test(subPlatform.id)) errors[`${path}.id`] = PLATFORM_ERROR.ID_FORMAT;
    else if (idCounts.get(subPlatform.id) > 1) errors[`${path}.id`] = PLATFORM_ERROR.ID_DUPLICATE;
    if (!subPlatform.nameFr) errors[`${path}.nameFr`] = PLATFORM_ERROR.REQUIRED;
    if (!subPlatform.nameEn) errors[`${path}.nameEn`] = PLATFORM_ERROR.REQUIRED;
    if (subPlatform.url && !isWebUrl(subPlatform.url)) errors[`${path}.url`] = PLATFORM_ERROR.URL;
  });

  return errors;
}
